import type { SupportedLanguage } from '@klareospace/pharos-lib-types';

export {
  DEFAULT_GC_TIME_MS,
  DEFAULT_STALE_TIME_MS,
  LIST_GC_TIME_MS,
  LIST_STALE_TIME_MS,
} from '@klareospace/pharos-lib-utils';
export { RTL_LANGUAGES } from '@klareospace/pharos-lib-types';

/** Native display names for language codes, used by language switchers. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  ar: 'العربية',
  en: 'English',
  fr: 'Français',
};

/** Default number of items per page for data tables. */
export const DEFAULT_TABLE_PAGE_SIZE = 10;

/** Default debounce delay (in ms) for search inputs. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Name of the admin-auth API client instance, as registered with `ApiClientProvider`. */
export const ADMIN_AUTH_API_CLIENT_NAME = 'admin-auth';

/** Name of the geo API client instance, as registered with `ApiClientProvider`. */
export const GEO_API_CLIENT_NAME = 'geo';

/** Default request timeout (in ms) for API clients and token-exchange calls. */
export const DEFAULT_HTTP_TIMEOUT_MS = 10_000;

/** Placeholder shown in place of an absent/undefined display value (em dash). */
export const EMPTY_VALUE_PLACEHOLDER = '—';

/** Session timeout (in ms) before a Pharos session is force-signed-out (12 hours). */
export const SESSION_TIMEOUT_MS = 12 * 60 * 60 * 1000;

/** Default page size used when fetching options for a dropdown/select field. */
export const DROPDOWN_FETCH_PAGE_SIZE = 100;

/** Default debounce delay (in ms) for uniqueness-check (async validation) inputs. */
export const UNIQUENESS_CHECK_DEBOUNCE_MS = 500;

/**
 * Status string used to represent a network error (no HTTP response) in telemetry dimensions.
 * Shared between web and mobile telemetry API loggers.
 */
export const TELEMETRY_NETWORK_ERROR_STATUS = 'NETWORK_ERROR';

/**
 * Names of every `customMetrics` series tracked via `TelemetryClient.trackMetric()`.
 * Single source of truth shared by web, mobile, and the terraform workbook tiles that
 * query these exact strings — never inline a metric name as a string literal.
 */
export const TELEMETRY_METRIC_NAMES = {
  BATTERY_LEVEL: 'mobile-battery-level',
  COLD_START_MS: 'mobile-cold-start-ms',
  CPU_USAGE_PERCENT: 'mobile-cpu-usage-percent',
  HTTP_REQUEST_DURATION_MS: 'http-request-duration-ms',
  JS_EVENT_LOOP_LAG_MS: 'mobile-js-event-loop-lag-ms',
  MEMORY_USED_MB: 'mobile-memory-used-mb',
} as const;
export type TelemetryMetricName = (typeof TELEMETRY_METRIC_NAMES)[keyof typeof TELEMETRY_METRIC_NAMES];

/**
 * Convenience type for the `data` returned by a paginated `useQuery()` call.
 * Typically `PaginatedResponse<T> | undefined` — this picks only the fields
 * that page-level hooks need to derive UI state.
 */
export type PaginatedQueryData<T> = { items: T[]; total: number; totalPages: number } | undefined;
